import {
  DragDropContext,
  Draggable,
  Droppable,
  OnDragEndResponder,
} from '@atlaskit/pragmatic-drag-and-drop-react-beautiful-dnd-migration'
import { PlusIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Control, useFieldArray, useFormContext } from 'react-hook-form'
import useSWR from 'swr'

import { Button } from '@/components/ui/button'
import { apiFetcher } from '@/data/fetcher'
import { Product } from '@/interfaces/catalog/Product'

import { DocumentFormValues } from '../DocumentForm'
import { DocumentItem } from './DocumentItem'

export interface DocumentArrayFieldsProps {
  control: Control<DocumentFormValues>
  currencySymbol: string
  currencyCode: string
  isVatPayer: boolean
}

export const DocumentArrayFields = ({ control, currencySymbol, currencyCode, isVatPayer }: DocumentArrayFieldsProps) => {
  const { setValue } = useFormContext<DocumentFormValues>()
  const [isDragDisabled, setIsDragDisabled] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)
  const { append, fields, move, remove } = useFieldArray({
    control,
    name: 'items',
  })

  const { data: productsResponse } = useSWR<{ data?: Product[]; total?: number }>(
    '/api/v2/products',
    apiFetcher,
  )
  const products = productsResponse?.data ?? []

  const defaultInvoiceItem: DocumentFormValues['items'][number] = {
    amount: 1,
    unit: '',
    description: null,
    unitPrice: null,
    taxRate: isVatPayer ? 21 : undefined,
    skuId: null,
  }

  const reorder: OnDragEndResponder = (result) => {
    const { source, destination } = result
    if (!destination) {
      return
    }
    const sourceIndex = source.index
    const destIndex = destination.index

    move(sourceIndex, destIndex)
  }

  const handleAppend = () => append(defaultInvoiceItem)

  // Enter v ceně posledního řádku přidá nový řádek a přesune kurzor do jeho popisu
  const handleAppendAndFocus = () => {
    append(defaultInvoiceItem, { shouldFocus: false })
    requestAnimationFrame(() => {
      const inputs = listRef.current?.querySelectorAll<HTMLInputElement>('[data-item-description]')
      inputs?.[inputs.length - 1]?.focus()
    })
  }

  const handleRemove = (index: number) => {
    if (fields.length === 1) return

    remove(index)
  }

  useEffect(() => {
    setIsDragDisabled(fields.length === 1)
  }, [fields])

  return (
    <>
      <DragDropContext onDragEnd={reorder}>
        <Droppable
          droppableId="table"
          direction="vertical"
          ignoreContainerClipping
          isDropDisabled={isDragDisabled}
          isCombineEnabled={false}
        >
          {(provided) => (
            <div
              ref={(el) => {
                provided.innerRef(el)
                listRef.current = el
              }}
              className="grid w-full"
              {...provided.droppableProps}
            >
              {fields.map((field, index) => {
                return (
                  <Draggable
                    key={field.id}
                    draggableId={field.id}
                    index={index}
                    isDragDisabled={isDragDisabled}
                  >
                    {(provided) => (
                      <DocumentItem
                        control={control}
                        setValue={setValue}
                        products={products}
                        field={field}
                        index={index}
                        hasRemoveButton={fields.length > 1}
                        onRemoveClick={() => handleRemove(index)}
                        ref={provided.innerRef}
                        dragHandleProps={provided.dragHandleProps}
                        isDragDisabled={isDragDisabled}
                        currencySymbol={currencySymbol}
                        currencyCode={currencyCode}
                        isVatPayer={isVatPayer}
                        onLastInputEnterKeyDown={index === fields.length - 1 ? handleAppendAndFocus : undefined}
                        {...provided.draggableProps}
                      />
                    )}
                  </Draggable>
                )
              })}

              {provided.placeholder as React.ReactNode}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <div className="pt-2">
        <Button type="button" variant="outline" onClick={handleAppend}>
          <PlusIcon className='size-4' />
          Přidat položku
        </Button>
      </div>
    </>
  )
}
